import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTrophy, FaMedal, FaStar, FaFire, FaChartBar,
  FaCheckCircle, FaVideo, FaCrown, FaSync
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/* ─── Medal colours & icons for top 3 ─────────────────────────── */
const RANK_CONFIG = {
  1: { color: '#FFD700', bg: 'from-yellow-400/20 to-yellow-600/10', border: 'border-yellow-400', shadow: 'shadow-yellow-400/30' },
  2: { color: '#C0C0C0', bg: 'from-gray-300/20 to-gray-400/10',   border: 'border-gray-400',   shadow: 'shadow-gray-400/30'   },
  3: { color: '#CD7F32', bg: 'from-orange-400/20 to-orange-600/10',border: 'border-orange-400', shadow: 'shadow-orange-400/30' }
};

function RankBadge({ rank }) {
  if (rank === 1) return <FaTrophy style={{ color: '#FFD700' }} className="text-2xl" />;
  if (rank === 2) return <FaMedal  style={{ color: '#C0C0C0' }} className="text-2xl" />;
  if (rank === 3) return <FaMedal  style={{ color: '#CD7F32' }} className="text-2xl" />;
  return <span className="text-base font-bold text-gray-500 dark:text-gray-400">#{rank}</span>;
}

function AnimatedNumber({ value, duration = 1200 }) {
  const [displayed, setDisplayed] = useState(0);
  const frame = useRef(null);
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(value * eased));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [value, duration]);
  return <>{displayed}</>;
}

function ScoreBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="font-medium text-gray-700 dark:text-gray-300">{value} pts</span>
      </div>
      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full ${color}`}
        />
      </div>
    </div>
  );
}

function PodiumCard({ entry, podiumHeight, rank, currentUserId }) {
  if (!entry) return null;
  const cfg  = RANK_CONFIG[rank];
  const isMe = entry.studentId === currentUserId;
  const emoji = rank === 1 ? '👑' : rank === 2 ? '🥈' : '🥉';
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.1 }}
      className="flex flex-col items-center flex-1 max-w-[120px]"
    >
      <div className="text-2xl mb-1">{emoji}</div>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-white text-lg shadow-md mb-2 bg-gradient-to-br from-orange-400 to-orange-600 ${isMe ? 'ring-2 ring-orange-400' : ''}`}>
        {entry.displayName?.charAt(0)?.toUpperCase() || '?'}
      </div>
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center mb-1 leading-tight">
        {entry.displayName}
        {isMe && <span className="block text-orange-500 text-xs">You</span>}
      </p>
      <p className="text-sm font-black mb-2" style={{ color: cfg.color }}>{entry.totalScore} pts</p>
      <div
        className={`w-full rounded-t-lg ${podiumHeight} flex items-center justify-center border-t-4`}
        style={{ background: `linear-gradient(180deg, ${cfg.color}33, ${cfg.color}11)`, borderColor: cfg.color }}
      >
        <span className="text-xl font-black" style={{ color: cfg.color }}>#{rank}</span>
      </div>
    </motion.div>
  );
}

const Scorecard = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('all');
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?._id || currentUser?.id;

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API_BASE}/leaderboard?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed to load leaderboard');
      setData(json.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeaderboard(); }, []);

  const leaderboard   = data?.leaderboard || [];
  const totalStudents = data?.totalStudents || 0;
  const myRank        = data?.myRank;
  const myScore       = data?.myScore || 0;
  const myEntry       = data?.myEntry;
  const myPercentile  = (totalStudents > 1 && myRank)
    ? Math.round(((totalStudents - myRank) / (totalStudents - 1)) * 100)
    : null;
  const maxScore      = leaderboard[0]?.totalScore || 1;
  const displayList   = filter === 'top10' ? leaderboard.slice(0, 10) : leaderboard;
  const myEntryInList = leaderboard.find(e => e.studentId === currentUserId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading leaderboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center max-w-md w-full">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Could not load leaderboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">{error}</p>
          <button onClick={fetchLeaderboard} className="flex items-center gap-2 mx-auto bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-700 transition-colors">
            <FaSync /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors duration-500">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <FaTrophy className="text-4xl text-yellow-500" />
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Leaderboard</h1>
          </div>
          <p className="text-gray-500 dark:text-gray-400">
            Rankings based on approved submissions &amp; completed videos across&nbsp;
            <span className="font-semibold text-orange-500">{totalStudents}</span> students
          </p>
        </motion.div>

        {/* My Rank Hero Card */}
        {myRank ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className={`relative overflow-hidden rounded-2xl p-6 mb-8 border-2 shadow-xl
              ${myRank <= 3
                ? `bg-gradient-to-br ${RANK_CONFIG[myRank].bg} ${RANK_CONFIG[myRank].border} ${RANK_CONFIG[myRank].shadow}`
                : 'bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10 border-orange-300 dark:border-orange-700'
              }`}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-400/10 rounded-full blur-3xl" />
            <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-orange-500 flex items-center justify-center text-3xl font-extrabold text-white shadow-lg">
                  {currentUser?.name?.charAt(0)?.toUpperCase() || 'S'}
                </div>
                {myRank <= 3 && (
                  <div className="absolute -top-2 -right-2 text-2xl">
                    {myRank === 1 ? '👑' : myRank === 2 ? '🥈' : '🥉'}
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 text-center sm:text-left">
                <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Your Rank</p>
                <div className="flex items-baseline gap-3 justify-center sm:justify-start">
                  <span className="text-5xl font-black text-gray-900 dark:text-white">#{myRank}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">of {totalStudents} students</span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 font-medium mt-1">{currentUser?.name || 'You'}</p>
                {myPercentile !== null && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1 font-medium">
                    🎯 Better than {myPercentile}% of all students
                  </p>
                )}
              </div>
              {/* Score */}
              <div className="text-center">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Total Score</p>
                <p className="text-4xl font-black text-orange-600 dark:text-orange-400">
                  <AnimatedNumber value={myScore} />
                </p>
                <p className="text-xs text-gray-400 mt-1">points</p>
              </div>
            </div>
            {/* Score bars */}
            <div className="relative z-10 mt-5 pt-5 border-t border-black/10 dark:border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <ScoreBar
                label="Approved submissions score"
                value={myEntryInList ? myEntryInList.totalScore - (myEntryInList.completedVideos * 5) : 0}
                max={maxScore}
                color="bg-orange-500"
              />
              <ScoreBar
                label="Completed videos score"
                value={myEntryInList ? myEntryInList.completedVideos * 5 : 0}
                max={maxScore}
                color="bg-blue-500"
              />
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8 border border-dashed border-orange-300 dark:border-orange-700 text-center"
          >
            <div className="text-5xl mb-3">🎯</div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Start Learning to Get Ranked!</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Complete videos and get submissions approved to appear on the leaderboard.</p>
          </motion.div>
        )}

        {/* Top-3 Podium */}
        {leaderboard.length >= 3 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaCrown className="text-yellow-500" /> Top 3 Champions
            </h2>
            <div className="flex items-end justify-center gap-3 sm:gap-6">
              <PodiumCard entry={leaderboard[1]} podiumHeight="h-24" rank={2} currentUserId={currentUserId} />
              <PodiumCard entry={leaderboard[0]} podiumHeight="h-32" rank={1} currentUserId={currentUserId} />
              <PodiumCard entry={leaderboard[2]} podiumHeight="h-16" rank={3} currentUserId={currentUserId} />
            </div>
          </motion.div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4">
          {['all', 'top10'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                filter === f
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-orange-400'
              }`}
            >
              {f === 'all' ? `All Students (${totalStudents})` : 'Top 10'}
            </button>
          ))}
          <button onClick={fetchLeaderboard} className="ml-auto p-2 text-gray-400 hover:text-orange-500 transition-colors" title="Refresh">
            <FaSync />
          </button>
        </div>

        {/* Leaderboard table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {/* Header */}
          <div className="grid grid-cols-[2.5rem_1fr_4rem_4rem_4.5rem] gap-2 sm:gap-4 items-center px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <span className="text-center">Rank</span>
            <span>Student</span>
            <span className="hidden sm:block text-center">Approved</span>
            <span className="hidden sm:block text-center">Videos</span>
            <span className="text-right">Score</span>
          </div>

          {/* Rows */}
          {displayList.length === 0 && (
            <div className="py-16 text-center text-gray-400 dark:text-gray-500">
              No data yet — be the first on the board! 🚀
            </div>
          )}
          {displayList.map((entry, index) => {
            const isMe = entry.studentId === currentUserId;
            return (
              <motion.div
                key={entry.studentId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.04 }}
                className={`grid grid-cols-[2.5rem_1fr_4rem_4rem_4.5rem] gap-2 sm:gap-4 items-center
                  px-4 sm:px-6 py-4 border-b border-gray-50 dark:border-gray-700/50 last:border-0 transition-colors
                  ${isMe ? 'bg-orange-50 dark:bg-orange-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
              >
                <div className="flex justify-center"><RankBadge rank={entry.rank} /></div>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0 bg-gradient-to-br from-orange-400 to-orange-600 ${isMe ? 'ring-2 ring-orange-300' : ''}`}>
                    {entry.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <p className={`font-semibold truncate ${isMe ? 'text-orange-700 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
                    {entry.displayName}
                    {isMe && <span className="ml-2 text-xs font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">You</span>}
                  </p>
                </div>
                <div className="hidden sm:flex justify-center">
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-lg">{entry.approvedVideos}</span>
                </div>
                <div className="hidden sm:flex justify-center">
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-lg">{entry.completedVideos}</span>
                </div>
                <div className="text-right">
                  <span className={`text-lg font-black ${
                    entry.rank === 1 ? 'text-yellow-500' :
                    entry.rank === 2 ? 'text-gray-400' :
                    entry.rank === 3 ? 'text-orange-500' :
                    isMe ? 'text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-gray-300'
                  }`}>{entry.totalScore}</span>
                  <p className="text-xs text-gray-400">pts</p>
                </div>
              </motion.div>
            );
          })}

          {/* Pinned "You" row when outside top-10 filter */}
          {myEntry && filter === 'top10' && (
            <div className="border-t-2 border-dashed border-orange-300 dark:border-orange-700">
              <div className="grid grid-cols-[2.5rem_1fr_4rem_4rem_4.5rem] gap-2 sm:gap-4 items-center px-4 sm:px-6 py-4 bg-orange-50 dark:bg-orange-900/20">
                <div className="flex justify-center"><span className="text-base font-bold text-orange-600">#{myEntry.rank}</span></div>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-orange-500 ring-2 ring-orange-300 flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
                    {myEntry.displayName?.charAt(0)?.toUpperCase() || 'Y'}
                  </div>
                  <p className="font-semibold truncate text-orange-700 dark:text-orange-400">
                    {myEntry.displayName}
                    <span className="ml-2 text-xs font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">You</span>
                  </p>
                </div>
                <div className="hidden sm:flex justify-center">
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-lg">{myEntry.approvedVideos}</span>
                </div>
                <div className="hidden sm:flex justify-center">
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-lg">{myEntry.completedVideos}</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-black text-orange-600 dark:text-orange-400">{myEntry.totalScore}</span>
                  <p className="text-xs text-gray-400">pts</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Scoring legend */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5"
        >
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <FaChartBar className="text-orange-500" /> How Points Are Calculated
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: '✅', label: 'Approved submission (with grade)',  pts: 'Grade × 10 pts', color: 'text-green-600'  },
              { icon: '📹', label: 'Completed video',                   pts: '+5 pts each',    color: 'text-blue-600'   },
              { icon: '📝', label: 'Approved (no grade yet)',            pts: '+50 pts flat',   color: 'text-orange-600' }
            ].map(item => (
              <div key={item.label} className="flex items-start gap-2 bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3">
                <span className="text-lg">{item.icon}</span>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                  <p className={`text-sm font-bold ${item.color}`}>{item.pts}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default Scorecard;
